import { ChangeDetectorRef, Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { Subject, takeUntil } from 'rxjs';

import type { Department } from '@core/departments-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { RequisitionFlow } from '@core/requisitions-types';
import type { DialogComponentI, SelectOption } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  FormFieldComponent,
  IconComponent,
  IconHelpComponent,
  InputComponent,
  LabelComponent,
  RtTooltip,
  SelectComponent,
} from '@core/user-interface-legacy';

import type { CreateRequisitionArgs } from '../../requisitions.types';

export type CreateBlankRequisitionDialogData = {
  withDepartments: boolean;
  approvalFlows: RequisitionFlow[];
  departments: Department[];
  isStep: boolean;
  saveHandler: (data: CreateRequisitionArgs) => void;
};

type DialogResult = {
  goBack: boolean;
};

@Component({
  selector: 'rt-requisitions-create-blank-requisition-dialog',
  templateUrl: './create-blank-requisition-dialog.component.html',
  styleUrls: ['./create-blank-requisition-dialog.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonComponent,
    ButtonLoadingDirective,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    FormFieldComponent,
    IconComponent,
    IconHelpComponent,
    InputComponent,
    LabelComponent,
    SelectComponent,
    TranslatePipe,
    ReactiveFormsModule,
    RtTooltip,
  ],
})
export class CreateBlankRequisitionDialogComponent implements DialogComponentI<
  CreateBlankRequisitionDialogData,
  DialogResult
> {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly dialogRef = inject(DialogRef);
  private readonly i18n = inject(I18nService);

  public form: FormGroup;
  public withDepartments: boolean;
  public isStep: boolean;
  public selectOptions: SelectOption[] = [];
  public saving = false;
  public texts = {
    subheading: '',
    label: '',
    placeholder: '',
    searchPlaceholder: '',
  };

  private saveHandler: (data: CreateRequisitionArgs) => void;
  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit() {
    this.form.valueChanges
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => this.cdRef.detectChanges());
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public setDialogData(data: CreateBlankRequisitionDialogData, triggerUpdate: boolean): void {
    this.isStep = data.isStep;
    this.withDepartments = data.withDepartments;
    this.selectOptions = this.withDepartments
      ? data.departments.map(department => ({
          label: department.name,
          value: department.id,
        }))
      : data.approvalFlows.map(flow => ({
          label: flow.title,
          value: flow.id!,
        }));
    this.saveHandler = data.saveHandler;

    this.createForm();
    this.setTexts();
  }

  public goBack() {
    this.dialogRef.close({
      goBack: true,
    });
  }

  public cancel() {
    this.dialogRef.close({ goBack: false });
  }

  public save() {
    this.saving = true;
    this.cdRef.detectChanges();

    const {
      value: { title, attributeId },
    } = this.form;

    const id = this.withDepartments
      ? {
          departmentId: attributeId ? Number.parseInt(attributeId, 10) : null,
        }
      : { approvalFlowId: Number.parseInt(attributeId, 10) };

    this.saveHandler({
      title,
      ...id,
    });
  }

  private createForm() {
    this.form = new FormGroup({
      title: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
      }),
      attributeId: new FormControl(null),
    });
    if (!this.withDepartments)
      this.form.controls['attributeId']!.setValidators([Validators.required]);
  }

  private setTexts() {
    this.texts = {
      subheading: this.i18n.translate(
        this.withDepartments
          ? 'app.requisitions.create_dialog.subheading_blank_departments'
          : 'app.requisitions.create_dialog.subheading_blank_flow',
      ),
      label: this.i18n.translate(
        this.withDepartments
          ? 'app.requisitions.create_dialog.department_label'
          : 'app.requisitions.create_dialog.flow_label',
      ),
      placeholder: this.i18n.translate(
        this.withDepartments
          ? 'app.requisitions.create_dialog.department_placeholder'
          : 'rtu.ui.select.placeholder',
      ),
      searchPlaceholder: this.i18n.translate(
        this.withDepartments
          ? 'app.requisitions.create_dialog.department_search'
          : 'app.requisitions.create_dialog.flow_search',
      ),
    };
  }
}
